import { Plus } from "lucide-react";
import { useT, useI18n } from "@/lib/i18n";

const COPY = {
  de: {
    intro:
      "Was unsere Kunden an Fritz Kamm AG schätzen, sind keine Schlagworte — es ist die tägliche Disziplin, mit der wir jedes Bauteil bearbeiten, jeden Termin halten und jede Charge dokumentieren.",
    pillars: [
      { n: "01", title: "Qualität und Termin", body: "Unser Leitziel seit jeher. Beidem ordnen wir alles unter — vom ersten Bad bis zur Auslieferung." },
      { n: "02", title: "Swiss Safety Center zertifiziert", body: "Unser Unternehmen arbeitet nach ISO 9001 und ISO 14001 — dokumentiert, geprüft und kontinuierlich verbessert." },
      { n: "03", title: "Nachhaltigkeit & CO₂", body: "Wir reduzieren CO₂-Emissionen aktiv und entwickeln unsere Prozesse nachhaltig weiter — Schweizer Verantwortung." },
      { n: "04", title: "Alles unter einem Dach", body: "Seit 2024 sind alle 11 Veredelungsverfahren am Standort Mels gebündelt — kurze Wege, eine Ansprechperson, durchgängige Qualitätssicherung über jeden Prozessschritt." },
      { n: "05", title: "Persönliche Fachberatung", body: "Fragen zu einem Verfahren oder zur Logistik? Unsere Fachkräfte stehen Ihnen direkt zur Verfügung — telefonisch oder im Werk." },
      { n: "06", title: "Logistik national & international", body: "Unsere Wege sind dezent geplant und national bis international ausgelegt — inklusive Transport- und Zusatzdienstleistungen." },
    ],
  },
  en: {
    intro:
      "What our customers value about Fritz Kamm AG isn't a tagline — it's the daily discipline with which we handle every part, hit every deadline and document every batch.",
    pillars: [
      { n: "01", title: "Quality and on-time delivery", body: "Our guiding principle since day one. Everything else takes second place — from the first bath to the shipment." },
      { n: "02", title: "Swiss Safety Center certified", body: "We operate to ISO 9001 and ISO 14001 — documented, audited and continuously improved." },
      { n: "03", title: "Sustainability & CO₂", body: "We actively reduce CO₂ emissions and continuously evolve our processes — Swiss responsibility in practice." },
      { n: "04", title: "Everything under one roof", body: "Since 2024 all 11 finishing processes have been consolidated at our Mels site — short distances, one point of contact, continuous quality assurance across every process step." },
      { n: "05", title: "Personal expert advice", body: "Questions about a process or about logistics? Our specialists are directly available — by phone or on-site." },
      { n: "06", title: "Logistics, domestic & international", body: "Our supply paths are planned with care and reach from regional to international — including transport and value-added services." },
    ],
  },
  fr: {
    intro:
      "Ce que nos clients apprécient chez Fritz Kamm SA, ce ne sont pas des slogans — c'est la rigueur quotidienne avec laquelle nous traitons chaque pièce, tenons chaque délai et documentons chaque lot.",
    pillars: [
      { n: "01", title: "Qualité et délais", body: "Notre exigence depuis toujours. Tout le reste vient ensuite — du premier bain jusqu'à la livraison." },
      { n: "02", title: "Certifié Swiss Safety Center", body: "Nous travaillons selon ISO 9001 et ISO 14001 — documenté, audité et continuellement amélioré." },
      { n: "03", title: "Durabilité & CO₂", body: "Nous réduisons activement les émissions de CO₂ et faisons évoluer nos procédés en continu — la responsabilité suisse en pratique." },
      { n: "04", title: "Tout sous un même toit", body: "Depuis 2024, l'ensemble des 11 procédés de finition est regroupé sur notre site de Mels — distances courtes, un interlocuteur unique, assurance qualité continue à chaque étape du processus." },
      { n: "05", title: "Conseil technique personnel", body: "Questions sur un procédé ou sur la logistique ? Nos spécialistes sont directement joignables — par téléphone ou sur place." },
      { n: "06", title: "Logistique nationale & internationale", body: "Nos circuits sont conçus avec soin et déploient une portée régionale jusqu'à internationale — transport et prestations annexes inclus." },
    ],
  },
  it: {
    intro:
      "Ciò che i nostri clienti apprezzano in Fritz Kamm SA non sono slogan — è la disciplina quotidiana con cui trattiamo ogni componente, rispettiamo ogni termine e documentiamo ogni lotto.",
    pillars: [
      { n: "01", title: "Qualità e puntualità", body: "Il nostro principio guida da sempre. Tutto il resto è subordinato — dal primo bagno fino alla consegna." },
      { n: "02", title: "Certificati Swiss Safety Center", body: "Lavoriamo secondo ISO 9001 e ISO 14001 — documentato, verificato e migliorato di continuo." },
      { n: "03", title: "Sostenibilità & CO₂", body: "Riduciamo attivamente le emissioni di CO₂ ed evolviamo costantemente i nostri processi — responsabilità svizzera in pratica." },
      { n: "04", title: "Tutto sotto un unico tetto", body: "Dal 2024 tutti gli 11 procedimenti di finitura sono concentrati nella sede di Mels — percorsi brevi, un solo interlocutore, controllo qualità continuo lungo ogni fase del processo." },
      { n: "05", title: "Consulenza tecnica personale", body: "Domande su un procedimento o sulla logistica? I nostri specialisti sono raggiungibili direttamente — al telefono o in stabilimento." },
      { n: "06", title: "Logistica nazionale e internazionale", body: "Le nostre vie sono pianificate con cura, dalla scala regionale a quella internazionale — incluso trasporto e servizi accessori." },
    ],
  },
};

export const WhyChooseUs = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  return (
    <section
      id="warum"
      data-testid="why-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="overline reveal mb-5">{t("sections.warum_overline", "— Warum Fritz Kamm AG")} · 03 / 07</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.warum_title_1", "Vertrauen wird")}
              <br />
              <span className="text-zinc-500">{t("sections.warum_title_2", "gemacht. Nicht versprochen.")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-xl">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-white/10">
          {c.pillars.map((p, i) => (
            <div
              key={p.n}
              data-testid={`why-card-${p.n}`}
              className={`reveal reveal-delay-${(i % 5) + 1} group relative p-10 md:p-12 border-r border-b border-white/10 hover:bg-[#141416] transition-colors duration-500`}
            >
              <div className="flex items-baseline gap-4 mb-6">
                <span className="font-display text-zinc-600 text-2xl">{p.n}</span>
                <span className="h-px flex-1 bg-white/10" />
                <Plus className="w-4 h-4 text-zinc-600 group-hover:rotate-90 group-hover:text-white transition-all duration-500" />
              </div>
              <h3 className="font-display text-2xl md:text-[28px] font-medium tracking-tight text-white mb-4">
                {p.title}
              </h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
